import React, { useEffect, useRef } from 'react';
import {
  Modal,
  Typography,
  Form,
  Input,
  Button,
  Select,
  Switch,
  useToast,
} from '@crisellevaldez/speculo-ui';
import { Play } from 'lucide-react';
import { Organization } from '../../../types/organization.types';
import { useUpdateOrganizationSettings } from '../../../hooks/organizations/useUpdateOrganizationSettings';
import { useVoiceSample } from '../../../hooks/useVoiceSample';

import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { normalizeVoiceSelection, useAiVoices } from '../../../hooks/useAiVoices';
import { useUser } from '../../../hooks/graphql/users/useUser';
import { AdminLockOverlay } from './AdminLockOverlay';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

interface CallSettings {
  voice: string;
  background_track: string;
  record: boolean;
  remi_intro_name?: string;
}

interface AssistantOrganization extends Organization {
  assistant_name?: string;
  assistant_name_pronunciation?: string;
  call_settings?: CallSettings;
}

interface AssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: AssistantOrganization;
  onSuccess?: () => void;
}

const backgroundOptions = [
  { value: 'none', label: 'No Background' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'office', label: 'Office' },
  { value: 'restaurant', label: 'Restaurant' },
];

const assistantSchema = z.object({
  assistant_name: z.string().min(1, 'Assistant name is required'),
  assistant_name_pronunciation: z.string().min(1, 'Pronunciation is required'),
  remi_intro_name: z.string().optional(),
  voice: z.string().min(1, 'Voice is required'),
  background_track: z.string().min(1, 'Background track is required'),
  record: z.boolean(),
});

type FormData = z.infer<typeof assistantSchema>;

export const AssistantModal: React.FC<AssistantModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  const { updateSettings, isPending } = useUpdateOrganizationSettings();
  const { isAdmin } = useUser();
  const { showToast } = useToast();
  const { playVoiceSample, isGenerating, isPlaying } = useVoiceSample('staging');

  const currentUser = useOrganizationStore((state) => state.currentUser);
  const { data: voices = [] } = useAiVoices({ enabled: isOpen });

  const {
    handleSubmit,
    reset,
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(assistantSchema),
    defaultValues: {
      assistant_name: '',
      assistant_name_pronunciation: '',
      voice: '',
      background_track: '',
      record: false,
    },
  });

  // Until the pronunciation is edited, it follows the assistant name.
  const pronunciationEdited = useRef(false);

  useEffect(() => {
    if (isOpen && selectedOrg?.call_settings) {
      pronunciationEdited.current = Boolean(selectedOrg.assistant_name_pronunciation);
      reset({
        assistant_name: selectedOrg.assistant_name || '',
        // Starts as the name itself; most names are said the way they're spelled.
        assistant_name_pronunciation:
          selectedOrg.assistant_name_pronunciation || selectedOrg.assistant_name || '',
        remi_intro_name: selectedOrg.remi_intro_name || selectedOrg.org_name || '',
        voice: normalizeVoiceSelection(selectedOrg.call_settings?.voice, voices),
        background_track: selectedOrg.call_settings?.background_track || '',
        record: selectedOrg.call_settings?.record || false,
      });
    }
  }, [isOpen, selectedOrg?.call_settings, selectedOrg, reset, voices]);

  const handlePlayVoiceSample = async (voiceId: string) => {
    if (!voiceId || !selectedOrg) {
      showToast({
        message: 'Please select a voice first',
        variant: 'error',
      });
      return;
    }

    const selectedVoice = voices.find((v) => v.voice_id === voiceId);
    const voiceName = selectedVoice?.name || 'Assistant';

    const userFirstName = currentUser?.displayName?.split(' ')[0] || 'there';
    let greetingText = `Hi ${userFirstName}, this is ${voiceName} from Speculo ey ay. I can call new real estate leads, schedule follow-ups, and reconnect with contacts from your CRM — helping your business grow while you focus on selling homes.`;

    if (greetingText.length > 200) {
      greetingText = `Hi ${userFirstName}, this is your AI assistant from Speculo. I call new leads, follow up with contacts, and help grow your business while you focus on closing deals.`;
    }

    try {
      await playVoiceSample(voiceId, greetingText);
      showToast({
        message: 'Playing voice sample...',
        variant: 'success',
      });
    } catch (error) {
      showToast({
        message: 'Failed to play voice sample',
        variant: 'error',
      });
    }
  };

  const onSubmit = async (data: FormData) => {
    if (!selectedOrg) return;

    try {
      await updateSettings({
        id: selectedOrg.id,
        data: {
          assistant_name: data.assistant_name,
          assistant_name_pronunciation: data.assistant_name_pronunciation,
          remi_intro_name: data.remi_intro_name || '',
          call_settings: {
            background_track: data.background_track,
            record: data.record,
            voice: data.voice,
          },
        },
      });

      // Call onSuccess first to fetch fresh data
      await onSuccess?.();

      showToast({
        message: 'Assistant settings updated successfully',
        variant: 'success',
      });
      onClose();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update assistant settings',
        variant: 'error',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">Assistant/AI</Typography>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <AdminLockOverlay locked={!isAdmin} subject="AI assistant settings">
          <Form.Control>
            <Form.Label>How should Remi introduce your company?</Form.Label>
            <Input {...register('remi_intro_name')} error={errors.remi_intro_name?.message} />
          </Form.Control>

          <div className="grid grid-cols-2 gap-4">
            <Form.Control required>
              <Form.Label>Assistant Name</Form.Label>
              <Input
                {...register('assistant_name', {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    if (!pronunciationEdited.current) {
                      setValue('assistant_name_pronunciation', e.target.value);
                    }
                  },
                })}
                placeholder="Enter assistant name"
                error={errors.assistant_name?.message}
                helperText="This is the name your assistant will use when introducing themselves to leads."
              />
            </Form.Control>
            <Form.Control required>
              <Form.Label>Name Pronunciation</Form.Label>
              <Input
                {...register('assistant_name_pronunciation', {
                  onChange: () => {
                    pronunciationEdited.current = true;
                  },
                })}
                placeholder="Enter pronunciation"
                error={errors.assistant_name_pronunciation?.message}
                helperText="Leave as-is unless the name is pronounced differently than it's spelled."
              />
            </Form.Control>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Control required>
              <Form.Label>Voice</Form.Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select
                    value={watch('voice')}
                    onChange={(val) => setValue('voice', (val as string) || '')}
                    error={errors.voice?.message}
                    options={voices.map((voice) => ({
                      value: voice.voice_id,
                      label: voice.displayLabel || `${voice.name} - ${voice.gender}`,
                    }))}
                    placeholder="Select voice"
                    disabled={!isAdmin}
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-9 gap-1"
                  onClick={() => {
                    const currentVoice = watch('voice');
                    if (currentVoice) {
                      handlePlayVoiceSample(currentVoice);
                    }
                  }}
                  disabled={!watch('voice') || isGenerating || isPlaying}
                >
                  {isGenerating ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-600 border-t-transparent" />
                  ) : isPlaying ? (
                    <div className="flex items-center gap-1">
                      <div
                        className="w-1 h-3 bg-yellow-600 animate-pulse rounded-full"
                        style={{ animationDelay: '0ms', animationDuration: '600ms' }}
                      />
                      <div
                        className="w-1 h-4 bg-yellow-600 animate-pulse rounded-full"
                        style={{ animationDelay: '150ms', animationDuration: '600ms' }}
                      />
                      <div
                        className="w-1 h-3 bg-yellow-600 animate-pulse rounded-full"
                        style={{ animationDelay: '300ms', animationDuration: '600ms' }}
                      />
                    </div>
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  {isGenerating ? 'Loading...' : isPlaying ? 'Playing...' : 'Test'}
                </Button>
              </div>
            </Form.Control>
            <Form.Control required>
              <Form.Label>Background Track</Form.Label>
              <Select
                value={watch('background_track')}
                onChange={(val) => setValue('background_track', (val as string) || '')}
                error={errors.background_track?.message}
                options={backgroundOptions}
                placeholder="Select background"
                disabled={!isAdmin}
              />
            </Form.Control>
          </div>

          <div className="flex items-center gap-2">
            <Form.Label className="mb-0">Allow Recording</Form.Label>
            <Controller
              control={control}
              name="record"
              render={({ field: { value, onChange } }) => (
                <Switch checked={value} onChange={onChange} size="sm" />
              )}
            />
          </div>
          </AdminLockOverlay>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} type="button">
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          isLoading={isPending}
          disabled={isPending || !isAdmin}
        >
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
